/**
 * Dynamic Component Loader for Bloomreach
 * @module component-loader
 */
import '@utils/anchor-scroll';
import { EXTERNAL_PACKAGES } from '@config/external-packages';
import { logger } from '@utils/logger';
import { getComponentImporter } from './component-manifest';
export const COMPONENT_INITIALIZATION_MEASURE_NAME = 'grenke.component-loader.component-initialization';
export const INTEGRATION_SCAN_MEASURE_NAME = 'grenke.component-loader.integration-scan';
export class ComponentInitializationCancelledError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ComponentInitializationCancelledError';
    }
}
const componentCache = new Map();
const dependencyCache = new Map();
const componentRecordsByElement = new WeakMap();
const componentRecords = new Set();
const postSettlementInitializationElements = new WeakSet();
const addedMutationScopes = new Set();
const removedMutationScopes = new Set();
const componentAttributeMutationElements = new Set();
const pendingIntegrationScopes = new Set();
const VISIBLE_COMPONENT_ROOT_MARGIN = '800px 0px';
let visibilityObserver = null;
let observer = null;
let mutationFlushScheduled = false;
let integrationScanPromise = null;
let performanceSpanSequence = 0;
function hasStaticDependencies(cls) {
    const candidate = cls;
    return Array.isArray(candidate.dependencies);
}
export function loadComponent(name) {
    if (!name)
        return Promise.reject(new Error('Component name required'));
    const cached = componentCache.get(name);
    if (cached) {
        logger.debug(`Component "${name}" already cached`);
        return cached;
    }
    const importer = getComponentImporter(name);
    if (!importer) {
        return Promise.reject(new Error(`Unknown component "${name}"`));
    }
    logger.info(`Loading component chunk: ${name}`);
    let loadPromise;
    loadPromise = importer()
        .then((module) => {
        if (!module.default || typeof module.default !== 'function') {
            throw new Error(`Component "${name}" must export a default class`);
        }
        logger.success(`Component loaded: ${name}`);
        return module;
    })
        .catch((error) => {
        if (componentCache.get(name) === loadPromise) {
            componentCache.delete(name);
        }
        throw error;
    });
    componentCache.set(name, loadPromise);
    return loadPromise;
}
export async function loadDependency(name) {
    if (!name)
        throw new Error('Dependency name required');
    const cached = dependencyCache.get(name);
    if (cached) {
        logger.info(`Using cached dependency: ${name}`);
        return await cached;
    }
    const loadPromise = (async () => {
        const config = EXTERNAL_PACKAGES[name];
        if (config) {
            logger.info(`Loading external package: ${name}`);
            const module = await config.import();
            const dependency = config.exportName === 'named'
                ? module
                : config.exportName && config.exportName !== 'default'
                    ? module[config.exportName]
                    : module.default ?? module[name] ?? module;
            if (!dependency)
                throw new Error(`Export not found in package "${name}"`);
            logger.success(`External package loaded: ${name}`);
            return dependency;
        }
        logger.info(`Loading internal dependency: ${name}`);
        const module = await loadComponent(name);
        return module.default;
    })();
    dependencyCache.set(name, loadPromise);
    try {
        return await loadPromise;
    }
    catch (error) {
        if (dependencyCache.get(name) === loadPromise) {
            dependencyCache.delete(name);
        }
        throw error;
    }
}
export async function loadDependencies(names) {
    const results = await Promise.all(names.map(async (name) => [name, await loadDependency(name)]));
    return Object.fromEntries(results);
}
export async function initializeComponent(element, componentName, options) {
    if (!(element instanceof Element))
        throw new Error('Invalid element');
    if (!componentName)
        throw new Error('Component name required');
    const existingRecord = componentRecordsByElement.get(element);
    if (existingRecord) {
        if (existingRecord.phase === 'ready') {
            if (existingRecord.componentName !== componentName) {
                throw new Error(`Element already has ready component "${existingRecord.componentName}" and cannot initialize "${componentName}"`);
            }
            element.setAttribute('data-component-initialized', 'true');
            logger.debug(`Component "${componentName}" already initialized on element`);
            return existingRecord.instance;
        }
        if (existingRecord.phase === 'initializing') {
            if (existingRecord.componentName !== componentName) {
                throw new Error(`Element is already initializing component "${existingRecord.componentName}" and cannot initialize "${componentName}"`);
            }
            logger.debug(`Component "${componentName}" is initializing, reusing in-flight work`);
            return existingRecord.initializationPromise;
        }
        if (existingRecord.phase === 'destroying') {
            await existingRecord.settledPromise;
            return await initializeComponent(element, componentName, options);
        }
        if (existingRecord.componentName !== componentName) {
            throw new Error(`Element is already scheduled for component "${existingRecord.componentName}" and cannot initialize "${componentName}"`);
        }
        existingRecord.options = options ?? existingRecord.options;
        return await startComponentInitialization(existingRecord);
    }
    const record = createComponentRecord(element, componentName, 'initializing', options);
    return await startComponentInitialization(record);
}
function createComponentRecord(element, componentName, phase, options) {
    if (componentRecordsByElement.has(element)) {
        throw new Error('A component lifecycle record already exists for this element');
    }
    const record = {
        element,
        componentName,
        phase,
        options,
        settledPromise: Promise.resolve(),
    };
    componentRecordsByElement.set(element, record);
    componentRecords.add(record);
    return record;
}
function startComponentInitialization(record) {
    visibilityObserver?.unobserve(record.element);
    record.phase = 'initializing';
    record.abortController = new AbortController();
    record.element.removeAttribute('data-component-initialized');
    record.element.removeAttribute('data-component-error');
    const initializationPromise = initializeComponentTransaction(record);
    record.initializationPromise = initializationPromise;
    record.settledPromise = initializationPromise.then(() => undefined, () => undefined);
    return initializationPromise;
}
async function initializeComponentTransaction(record) {
    const { element, componentName } = record;
    const span = beginPerformanceSpan(COMPONENT_INITIALIZATION_MEASURE_NAME);
    let outcome = 'error';
    let instance;
    logger.info(`Initializing component: ${componentName}`);
    try {
        const module = await loadComponent(componentName);
        ensureInitializationActive(record);
        const ComponentClass = module.default;
        let dependencies;
        if (hasStaticDependencies(ComponentClass)) {
            logger.info(`Loading ${ComponentClass.dependencies.length} dependencies for ${componentName}:`, ComponentClass.dependencies);
            dependencies = await loadDependencies(ComponentClass.dependencies);
            ensureInitializationActive(record);
        }
        instance = new ComponentClass(element, record.options, dependencies);
        record.instance = instance;
        const context = { signal: record.abortController?.signal };
        await instance.init(context);
        ensureInitializationActive(record);
        record.phase = 'ready';
        element.removeAttribute('data-component-error');
        element.setAttribute('data-component-initialized', 'true');
        outcome = 'success';
        logger.success(`Component initialized: ${componentName}`);
        return instance;
    }
    catch (error) {
        const cancellationWasRequested = isInitializationCancelled(record);
        record.abortController?.abort();
        if (instance) {
            try {
                await instance.destroy?.();
            }
            catch (destroyError) {
                logger.error(`Failed to roll back "${componentName}" after initialization error:`, destroyError);
            }
        }
        record.instance = undefined;
        const cancelled = cancellationWasRequested
            || record.phase !== 'initializing'
            || componentRecordsByElement.get(record.element) !== record;
        if (cancelled) {
            outcome = 'cancelled';
            clearComponentMarkers(element);
            logger.debug(`Initialization cancelled: ${componentName}`);
        }
        else {
            element.removeAttribute('data-component-initialized');
            element.setAttribute('data-component-error', 'true');
            logger.error(`Failed to initialize "${componentName}":`, error);
        }
        removeComponentRecord(record);
        if (cancelled) {
            throw error instanceof ComponentInitializationCancelledError
                ? error
                : new ComponentInitializationCancelledError(`Initialization cancelled for component "${componentName}"`);
        }
        throw error;
    }
    finally {
        finishPerformanceSpan(span, {
            componentName,
            outcome,
        });
    }
}
function ensureInitializationActive(record) {
    if (isInitializationCancelled(record)) {
        throw new ComponentInitializationCancelledError(`Initialization cancelled for component "${record.componentName}"`);
    }
}
function isInitializationCancelled(record) {
    return record.phase !== 'initializing'
        || componentRecordsByElement.get(record.element) !== record
        || record.abortController?.signal.aborted === true;
}
function removeComponentRecord(record) {
    if (componentRecordsByElement.get(record.element) === record) {
        componentRecordsByElement.delete(record.element);
    }
    componentRecords.delete(record);
}
function initializeElement(element) {
    const name = element.getAttribute('data-component');
    if (!name)
        return;
    let options;
    const optionsAttribute = element.getAttribute('data-component-options');
    if (optionsAttribute) {
        try {
            options = JSON.parse(optionsAttribute);
        }
        catch {
            logger.error(`Invalid JSON in data-component-options for "${name}"`);
        }
    }
    void initializeComponent(element, name, options).catch((error) => {
        if (error instanceof ComponentInitializationCancelledError)
            return;
        logger.error(`Auto-init failed for "${name}":`, error);
    });
}
function scheduleElementInitialization(element) {
    const name = element.getAttribute('data-component');
    if (!name)
        return;
    let record = componentRecordsByElement.get(element);
    if (element.hasAttribute('data-component-initialized') && record?.phase !== 'ready') {
        element.removeAttribute('data-component-initialized');
    }
    if (record?.phase === 'ready') {
        if (record.componentName === name) {
            element.setAttribute('data-component-initialized', 'true');
            return;
        }
        requestComponentDestruction(record);
        scheduleInitializationAfterSettlement(element, record);
        return;
    }
    if (record?.phase === 'visible' && record.componentName !== name) {
        visibilityObserver?.unobserve(element);
        removeComponentRecord(record);
        record = undefined;
    }
    if (record?.phase === 'visible')
        return;
    if (record?.phase === 'destroying') {
        scheduleInitializationAfterSettlement(element, record);
        return;
    }
    if (!record && element.getAttribute('data-component-load') === 'visible') {
        const intersectionObserver = getVisibilityObserver();
        if (intersectionObserver) {
            createComponentRecord(element, name, 'visible', readComponentOptions(element, name));
            intersectionObserver.observe(element);
            return;
        }
    }
    initializeElement(element);
}
function scheduleInitializationAfterSettlement(element, record) {
    if (postSettlementInitializationElements.has(element))
        return;
    postSettlementInitializationElements.add(element);
    void record.settledPromise.then(() => {
        postSettlementInitializationElements.delete(element);
        if (element.isConnected && element.hasAttribute('data-component')) {
            scheduleElementInitialization(element);
        }
    });
}
function readComponentOptions(element, componentName) {
    const optionsAttribute = element.getAttribute('data-component-options');
    if (!optionsAttribute)
        return undefined;
    try {
        return JSON.parse(optionsAttribute);
    }
    catch {
        logger.error(`Invalid JSON in data-component-options for "${componentName}"`);
        return undefined;
    }
}
function getVisibilityObserver() {
    if (visibilityObserver)
        return visibilityObserver;
    if (typeof IntersectionObserver !== 'function')
        return null;
    visibilityObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
            if (!entry.isIntersecting)
                continue;
            visibilityObserver?.unobserve(entry.target);
            const record = componentRecordsByElement.get(entry.target);
            if (record?.phase !== 'visible')
                continue;
            if (entry.target.isConnected) {
                initializeElement(entry.target);
            }
            else {
                requestComponentDestruction(record);
            }
        }
    }, { rootMargin: VISIBLE_COMPONENT_ROOT_MARGIN });
    return visibilityObserver;
}
export function autoInitializeComponents() {
    const elements = document.querySelectorAll('[data-component]');
    logger.info(`Found ${elements.length} component(s) to initialize`);
    elements.forEach(scheduleElementInitialization);
}
export function startComponentObserver() {
    if (observer)
        return;
    observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            if (mutation.type === 'attributes') {
                componentAttributeMutationElements.add(mutation.target);
                continue;
            }
            for (const node of mutation.addedNodes) {
                if (node.nodeType !== Node.ELEMENT_NODE)
                    continue;
                addDisjointElementScope(addedMutationScopes, node);
            }
            for (const node of mutation.removedNodes) {
                if (node.nodeType !== Node.ELEMENT_NODE)
                    continue;
                addDisjointElementScope(removedMutationScopes, node);
            }
        }
        scheduleMutationFlush();
    });
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-component'],
    });
    resumeVisibleElementInitialization();
    logger.info('MutationObserver started (watching for dynamic components)');
}
export function stopComponentObserver() {
    observer?.disconnect();
    observer = null;
    visibilityObserver?.disconnect();
    visibilityObserver = null;
    addedMutationScopes.clear();
    removedMutationScopes.clear();
    componentAttributeMutationElements.clear();
    mutationFlushScheduled = false;
}
function resumeVisibleElementInitialization() {
    for (const record of [...componentRecords]) {
        if (record.phase !== 'visible')
            continue;
        if (!record.element.isConnected) {
            requestComponentDestruction(record);
            continue;
        }
        const intersectionObserver = getVisibilityObserver();
        if (intersectionObserver) {
            intersectionObserver.observe(record.element);
        }
        else {
            initializeElement(record.element);
        }
    }
}
function addDisjointElementScope(scopes, scope) {
    for (const existingScope of scopes) {
        if (existingScope === scope || existingScope.contains(scope))
            return;
        if (scope.contains(existingScope))
            scopes.delete(existingScope);
    }
    scopes.add(scope);
}
function scheduleMutationFlush() {
    if (mutationFlushScheduled)
        return;
    mutationFlushScheduled = true;
    queueMicrotask(() => {
        mutationFlushScheduled = false;
        flushMutationScopes();
    });
}
function flushMutationScopes() {
    const addedScopes = [...addedMutationScopes];
    const removedScopes = [...removedMutationScopes];
    const componentAttributeElements = [...componentAttributeMutationElements];
    addedMutationScopes.clear();
    removedMutationScopes.clear();
    componentAttributeMutationElements.clear();
    for (const scope of removedScopes) {
        if (!scope.isConnected) {
            void destroyIntegrations(scope).catch((error) => {
                logger.error('Failed to destroy integrations in removed scope:', error);
            });
        }
    }
    if (removedScopes.length > 0) {
        destroyDisconnectedComponents();
    }
    const connectedAddedScopes = addedScopes.filter((scope) => scope.isConnected);
    const addedElements = collectComponentElements(connectedAddedScopes);
    for (const element of componentAttributeElements) {
        const record = componentRecordsByElement.get(element);
        const componentName = element.getAttribute('data-component');
        if (record && (!element.isConnected || record.componentName !== componentName)) {
            requestComponentDestruction(record);
        }
        if (!componentName) {
            clearComponentMarkers(element);
        }
        else if (element.isConnected) {
            addedElements.add(element);
        }
    }
    for (const element of addedElements) {
        scheduleElementInitialization(element);
    }
    for (const scope of connectedAddedScopes) {
        void scheduleIntegrationScan(scope);
    }
}
function collectComponentElements(scopes) {
    const elements = new Set();
    for (const scope of scopes) {
        if (scope.hasAttribute('data-component')) {
            elements.add(scope);
        }
        scope.querySelectorAll('[data-component]').forEach((element) => elements.add(element));
    }
    return elements;
}
function destroyDisconnectedComponents() {
    for (const record of [...componentRecords]) {
        if (!record.element.isConnected)
            requestComponentDestruction(record);
    }
}
export function destroyComponentInstance(componentName, element) {
    const record = componentRecordsByElement.get(element);
    if (!record || record.componentName !== componentName)
        return false;
    requestComponentDestruction(record);
    return true;
}
export function destroyComponent(componentName) {
    for (const record of [...componentRecords]) {
        if (record.componentName === componentName) {
            requestComponentDestruction(record);
        }
    }
}
export function destroyAllComponents() {
    for (const record of [...componentRecords]) {
        requestComponentDestruction(record);
    }
}
function requestComponentDestruction(record) {
    if (componentRecordsByElement.get(record.element) !== record)
        return;
    const previousPhase = record.phase;
    visibilityObserver?.unobserve(record.element);
    clearComponentMarkers(record.element);
    if (previousPhase === 'destroying')
        return;
    if (previousPhase === 'visible') {
        removeComponentRecord(record);
        return;
    }
    record.phase = 'destroying';
    record.abortController?.abort();
    if (previousPhase === 'initializing') {
        // The initialization transaction owns rollback and keeps this record
        // exclusive until the component's (possibly async) destroy completes.
        return;
    }
    record.settledPromise = destroyReadyComponent(record);
}
async function destroyReadyComponent(record) {
    try {
        await record.instance?.destroy?.();
    }
    catch (error) {
        logger.error(`Failed to destroy "${record.componentName}":`, error);
    }
    finally {
        record.instance = undefined;
        removeComponentRecord(record);
    }
}
function clearComponentMarkers(element) {
    element.removeAttribute('data-component-initialized');
    element.removeAttribute('data-component-error');
}
export function getComponentInstances(componentName) {
    return [...componentRecords]
        .filter((record) => record.componentName === componentName && record.phase === 'ready')
        .map((record) => record.instance);
}
export async function preloadComponents(names) {
    await Promise.allSettled(names.map(loadComponent));
}
async function bootstrap() {
    autoInitializeComponents();
    startComponentObserver();
    await scheduleIntegrationScan(document);
}
async function initializeIntegrations(scope) {
    const { initializeIntegrations: initIntegrations } = await import(
    /* webpackChunkName: "integration-loader" */ '@integrations/integration-loader');
    await initIntegrations(scope);
}
async function destroyIntegrations(scope) {
    const { destroyIntegrations: destroyWithinScope } = await import(
    /* webpackChunkName: "integration-loader" */ '@integrations/integration-loader');
    await destroyWithinScope(scope);
}
function scheduleIntegrationScan(scope) {
    addIntegrationScanScope(scope);
    if (integrationScanPromise)
        return integrationScanPromise;
    integrationScanPromise = runIntegrationScanQueue()
        .finally(() => {
        integrationScanPromise = null;
        if (pendingIntegrationScopes.size > 0) {
            void scheduleIntegrationScanQueueContinuation();
        }
    });
    return integrationScanPromise;
}
function scheduleIntegrationScanQueueContinuation() {
    const firstPendingScope = pendingIntegrationScopes.values().next().value;
    return firstPendingScope ? scheduleIntegrationScan(firstPendingScope) : Promise.resolve();
}
function addIntegrationScanScope(scope) {
    if (scope === document) {
        pendingIntegrationScopes.clear();
        pendingIntegrationScopes.add(document);
        return;
    }
    if (pendingIntegrationScopes.has(document))
        return;
    for (const pendingScope of pendingIntegrationScopes) {
        if (!(pendingScope instanceof Element))
            continue;
        if (pendingScope === scope || pendingScope.contains(scope))
            return;
        if (scope.contains(pendingScope))
            pendingIntegrationScopes.delete(pendingScope);
    }
    pendingIntegrationScopes.add(scope);
}
async function runIntegrationScanQueue() {
    while (pendingIntegrationScopes.size > 0) {
        const scopes = [...pendingIntegrationScopes].filter((scope) => (scope === document || (scope instanceof Element && scope.isConnected)));
        pendingIntegrationScopes.clear();
        if (scopes.length === 0)
            continue;
        const span = beginPerformanceSpan(INTEGRATION_SCAN_MEASURE_NAME);
        let outcome = 'success';
        try {
            await Promise.all(scopes.map(initializeIntegrations));
        }
        catch (error) {
            outcome = 'error';
            logger.error('Failed to initialize integrations:', error);
        }
        finally {
            finishPerformanceSpan(span, {
                outcome,
                scopeCount: scopes.length,
                scopeType: scopes[0] === document ? 'document' : 'element',
            });
        }
    }
}
function beginPerformanceSpan(name) {
    if (typeof performance === 'undefined'
        || typeof performance.mark !== 'function'
        || typeof performance.measure !== 'function') {
        return null;
    }
    const id = ++performanceSpanSequence;
    const span = {
        name,
        startMark: `${name}:start:${id}`,
        endMark: `${name}:end:${id}`,
    };
    try {
        performance.mark(span.startMark);
        return span;
    }
    catch {
        return null;
    }
}
function finishPerformanceSpan(span, detail) {
    if (!span || typeof performance === 'undefined')
        return;
    try {
        performance.mark(span.endMark);
        try {
            performance.measure(span.name, {
                start: span.startMark,
                end: span.endMark,
                detail,
            });
        }
        catch {
            // Safari versions without PerformanceMeasureOptions still support
            // the mark-name overload; detail is unavailable in that fallback.
            performance.measure(span.name, span.startMark, span.endMark);
        }
    }
    catch {
        // Performance instrumentation must never affect component lifecycle.
    }
    finally {
        try {
            performance.clearMarks(span.startMark);
            performance.clearMarks(span.endMark);
        }
        catch {
            // Some partial Performance implementations omit clearMarks.
        }
    }
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
}
else {
    void bootstrap();
}
export const ComponentLoader = {
    loadComponent,
    loadDependency,
    loadDependencies,
    initializeComponent,
    autoInitializeComponents,
    startComponentObserver,
    stopComponentObserver,
    destroyComponentInstance,
    destroyComponent,
    destroyAllComponents,
    getComponentInstances,
    preloadComponents,
};
window.ComponentLoader = ComponentLoader;
